import type { DeleteProjectModalProps } from "@v2/types/modalTypes";
import type { ProjectIssueType } from "schemas/generated/project";

import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import { Button } from "@replo/design-system/components/button/Button";
import { Drawer } from "@replo/design-system/components/drawer/Drawer";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldTitle,
} from "@replo/design-system/components/field/Field";
import { Input } from "@replo/design-system/components/input/Input";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { toast } from "@replo/design-system/components/toast/Toast";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import { skipToken } from "@tanstack/react-query";
import { Loader } from "@v2/components/Loader";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { projectRouter, projectUtils } from "@v2/trpc/routes/project";
import { userUtils } from "@v2/trpc/routes/user";
import { workspaceUtils } from "@v2/trpc/routes/workspace";
import { getProjectLabels } from "@v2/utils/project-labels";
import { getProjectName } from "@v2/utils/project-utils";
import { routes } from "@v2/utils/router";
import { useForm } from "react-hook-form";
import { generatePath, Link, useNavigate } from "react-router-dom";
import { exhaustiveSwitch, isEmpty } from "replo-utils/lib/misc";
import z from "zod";

type FormValues = {
  projectName: string;
};

const getIssueNavigationInfo = (
  issueType: ProjectIssueType,
  projectId: string,
): { path: string; linkText: string } => {
  return exhaustiveSwitch({ type: issueType })({
    publishedElements: {
      path: generatePath(routes.shops.site, { projectId }),
      linkText: "Go to Editor",
    },
    shopifyIntegration: {
      path: generatePath(routes.shops.settings.shops.integrations.home, {
        projectId,
      }),
      linkText: "Go to Integrations",
    },
    experiments: {
      path: generatePath(routes.shops.experiments.list, { projectId }),
      linkText: "Go to A/B Tests",
    },
  });
};

export const DeleteProjectModal: React.FC<
  DeleteProjectModalProps & {
    open: boolean;
    onOpenChange: (open: boolean) => void;
  }
> = ({ projectId, callback, from, open, onOpenChange }) => {
  const isMobile = useIsMobile();
  // NOTE (Fran 2024-08-29): We don't want to use the current project context here because this will
  // depend on which project the user clicks on in the dashboard. So, we only want to get the project
  // the user needs to delete.
  const { data: projectData, isLoading: isLoadingProject } =
    projectRouter.getV2.useQuery(projectId ?? skipToken);
  const project = projectData?.project;

  const labels = getProjectLabels(project?.hostingType);
  const projectLabel = labels.singular;
  const projectLabelCapitalized = labels.singularCapitalized;

  const { data: canDeleteData, isLoading: isCheckingCanDelete } =
    projectRouter.canDelete.useQuery(projectId ? { projectId } : skipToken);

  const logEvent = useLogAnalytics();
  const navigate = useNavigate();
  const { mutate: deleteProject, isPending } = projectRouter.delete.useMutation(
    {
      onSuccess: (data, { projectId }) => {
        toast({ title: `${projectLabelCapitalized} deleted` });
        onOpenChange(false);
        callback?.();
        logEvent("project.delete", {
          projectId: projectId,
          projectName: projectNameFromData,
          from,
        });
        void workspaceUtils.canDelete.invalidate();
        void projectUtils.canDelete.invalidate();
        void workspaceUtils.getUserWorkspacesList.invalidate();
        void projectUtils.findByUserId.invalidate();
        void projectUtils.listWithStats.invalidate();
        void projectUtils.listWithIdAndType.invalidate();
        void userUtils.get.invalidate();
        void projectUtils.getV2.invalidate();
        void projectUtils.listGroupedByWorkspaceForUser.invalidate();

        // If there's no available project (last project was deleted), redirect to workspace settings
        if (data.availableProjectId) {
          navigate(
            generatePath(routes.shops.site, {
              projectId: data.availableProjectId,
            }),
          );
        } else {
          navigate(
            generatePath("/workspace/:workspaceId/settings/general", {
              workspaceId: data.workspaceId,
            }),
          );
        }
      },
    },
  );
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    clearErrors,
  } = useForm<FormValues>({
    mode: "onChange",
    defaultValues: {
      projectName: "",
    },
    resolver: zodResolver(
      z.object({
        projectName: z
          .string()
          .min(1, `Please type the name of the project.`)
          .refine((value) => value === projectNameFromData, {
            message: `The name of the project is incorrect.`,
          }),
      }),
    ),
  });
  const hasProjectNameErrors = !isEmpty(errors.projectName);
  // NOTE (Fran 2024-05-02): There were cases where the user created a project with a space at the end
  // but is not super obvious in the UI, so we are trimming the project name to avoid this problem.
  const projectNameFromData = getProjectName(project).trim();

  const canDelete = canDeleteData?.canDelete ?? false;
  const issues = canDeleteData?.issues ?? [];

  const onSubmit = ({ projectName }: FormValues) => {
    if (projectNameFromData === projectName && project?.id) {
      void deleteProject({ projectId: project.id });
    }
  };

  const canSubmitProjectForm = canDelete && isValid;

  const onSubmitForm = (data: React.FormEvent) => {
    if (!canSubmitProjectForm) {
      return;
    }
    data.preventDefault();
    void handleSubmit(onSubmit)(data);
  };

  const issueCountText =
    issues.length === 1
      ? "One issue prevents"
      : `${issues.length} issues prevent`;

  const description = !canDelete
    ? `${issueCountText} this ${projectLabel} from being deleted:`
    : `Deleting this ${projectLabel} will erase it. You will not be able to recover it without the help of the Replo support team.`;

  const modalContent = (() => {
    if (isLoadingProject || isCheckingCanDelete) {
      return (
        <div className="mx-auto w-16 py-4">
          <Loader />
        </div>
      );
    }

    if (!canDelete && issues.length > 0) {
      return (
        <div className="flex flex-col gap-2">
          {issues.map((issue) => {
            const navInfo = projectId
              ? getIssueNavigationInfo(issue.type, projectId)
              : null;
            return (
              <div
                key={issue.type}
                className="border-0.5 border-border rounded-lg"
              >
                <Field orientation="horizontal" className="items-start p-3">
                  <FieldContent>
                    <FieldTitle>
                      {exhaustiveSwitch(issue)({
                        publishedElements: () => "Published Items",
                        shopifyIntegration: () => "Shopify Integration",
                        experiments: () => "A/B Tests",
                      })}
                    </FieldTitle>
                    <FieldDescription>{issue.message}</FieldDescription>
                  </FieldContent>
                  {navInfo && (
                    <div className="flex justify-end">
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => navigate(navInfo.path)}
                      >
                        {navInfo.linkText}
                      </Button>
                    </div>
                  )}
                </Field>
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-2">
        <div className="text-muted flex flex-col text-sm">
          <span>Type "{projectNameFromData}" to confirm deletion.</span>
        </div>
        <LabeledControl
          label={`${projectLabelCapitalized} Name`}
          id="projectName"
          error={errors.projectName?.message}
        >
          <Input
            id="projectName"
            autoComplete="off"
            placeholder={projectNameFromData}
            {...register("projectName", {
              // NOTE (Fran 2024-05-02): We should clear the errors when the user is typing
              onChange: () => clearErrors("projectName"),
            })}
            type="text"
            size="base"
            invalid={hasProjectNameErrors}
            autoFocus
          />
        </LabeledControl>
      </div>
    );
  })();

  const title = `Delete ${projectLabelCapitalized}`;

  const footer = (
    <div className="flex flex-row justify-end gap-2 w-full">
      <Button
        variant="secondary"
        size="base"
        onClick={() => onOpenChange(false)}
      >
        Cancel
      </Button>
      <Button
        variant="destructive"
        size="base"
        render={<button type="submit" form="delete-project-form" />}
        disabled={!canSubmitProjectForm}
        loading={isPending}
      >
        Delete
      </Button>
    </div>
  );

  const formContent = (
    <form
      id="delete-project-form"
      className="flex flex-col gap-6"
      onSubmit={onSubmitForm}
    >
      <div className="flex flex-col gap-6">
        {canDelete && project?.ownerWorkspaceId && (
          <p className="text-default text-sm">
            Your subscription won't be affected. To remove billed{" "}
            {labels.plural}, go to your{" "}
            <Link
              className="text-primary text-sm font-medium underline"
              to={generatePath(routes.shops.settings.workspace.billing, {
                projectId: project.id,
              })}
            >
              Workspace Billing Settings
            </Link>
            .
          </p>
        )}
        {modalContent}
      </div>
    </form>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        title={title}
        description={description}
        footer={footer}
      >
        {formContent}
      </Drawer>
    );
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      size="base"
      description={description}
      footer={footer}
    >
      {formContent}
    </Modal>
  );
};
